/**
 * Meta Pixel (Facebook) — carga condicional vía VITE_META_PIXEL_ID.
 * Sin ID configurado, todas las llamadas son no-op (dev local sin pixel).
 */

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

type Fbq = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
  push: Fbq;
};

const pixelId = (import.meta.env.VITE_META_PIXEL_ID ?? "").trim();

let initialized = false;

/** Devuelve true si el pixel está configurado y cargado. */
export function isMetaPixelEnabled(): boolean {
  return pixelId.length > 0 && !pixelId.includes("REEMPLAZA");
}

function injectNoscriptFallback(id: string): void {
  const img = document.createElement("img");
  img.height = 1;
  img.width = 1;
  img.style.display = "none";
  img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(id)}&ev=PageView&noscript=1`;
  const noscript = document.createElement("noscript");
  noscript.appendChild(img);
  document.body.appendChild(noscript);
}

/** Carga fbevents.js e inicializa el pixel. Seguro llamar varias veces. */
export function initMetaPixel(): boolean {
  if (!isMetaPixelEnabled()) return false;
  if (initialized) return true;

  if (!window.fbq) {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue.push(args);
      }
    } as Fbq;
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.push = fbq;
    window.fbq = fbq;
    window._fbq = fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    const first = document.getElementsByTagName("script")[0];
    first?.parentNode?.insertBefore(script, first);
  }

  window.fbq!("init", pixelId);
  initialized = true;
  injectNoscriptFallback(pixelId);
  return true;
}

function track(event: string, params?: Record<string, unknown>): void {
  if (!initialized || !window.fbq) return;
  if (params) {
    window.fbq("track", event, params);
  } else {
    window.fbq("track", event);
  }
}

export function trackMetaPageView(): void {
  track("PageView");
}

export function trackMetaViewContent(contentName: string): void {
  track("ViewContent", { content_name: contentName });
}

export function trackMetaLead(params: {
  content_name: string;
  content_category?: string;
}): void {
  track("Lead", {
    content_name: params.content_name,
    ...(params.content_category
      ? { content_category: params.content_category }
      : {}),
  });
}

export function trackMetaCompleteRegistration(): void {
  track("CompleteRegistration");
}

export function trackMetaInitiateCheckout(params: {
  value: number;
  currency: "COP";
  content_name: string;
}): void {
  track("InitiateCheckout", {
    value: params.value,
    currency: params.currency,
    content_name: params.content_name,
  });
}

export function trackMetaPurchase(params: {
  value: number;
  currency: "COP";
  content_name: string;
}): void {
  track("Purchase", {
    value: params.value,
    currency: params.currency,
    content_name: params.content_name,
  });
}
